// services/SemanticModelService.js
import { fetchAuthSession } from '@aws-amplify/auth';

import env from '../config/env';
import { BACKEND_SEMANTIC_MODEL_API } from '../env';

const API_BASE = BACKEND_SEMANTIC_MODEL_API;
const BASE_URL = `${API_BASE}/semantic-models`;

const SEMANTIC_MODEL_API_KEY =
  env?.API_KEY ||
  (typeof process !== 'undefined' ? process.env.REACT_APP_SEMANTIC_MODEL_API_KEY : undefined) ||
  (typeof process !== 'undefined' ? process.env.REACT_APP_X_API_KEY : undefined);

const defaultHeaders = {
  Accept: 'application/json',
  ...(SEMANTIC_MODEL_API_KEY ? { 'x-api-key': SEMANTIC_MODEL_API_KEY } : {}),
};

if (!SEMANTIC_MODEL_API_KEY) {
  // eslint-disable-next-line no-console
  console.warn(
    'SemanticModelService: no se detectó API key (REACT_APP_SEMANTIC_MODEL_API_KEY o REACT_APP_X_API_KEY). ' +
      'Las llamadas al backend podrían responder 401/CORS.'
  );
}

const NAME_MIN_LENGTH = 3;
const NAME_MAX_LENGTH = 256;

function coalesce(...values) {
  for (const value of values) {
    if (value !== undefined && value !== null) {
      return value;
    }
  }
  return undefined;
}

function mapArrayOfIdentifiers(value, key) {
  if (value === undefined || value === null) {
    return undefined;
  }

  const arrayValue = Array.isArray(value) ? value : [value];

  if (arrayValue.length === 0) {
    return [];
  }

  const aliasUpper = key.toUpperCase();
  const aliasCamel = key.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
  const aliasPascal = aliasCamel.charAt(0).toUpperCase() + aliasCamel.slice(1);

  const normalized = arrayValue
    .map((entry) => {
      if (entry === undefined || entry === null || entry === '') {
        return null;
      }

      if (typeof entry === 'object' && !Array.isArray(entry)) {
        const identifier = coalesce(
          entry[key],
          entry[aliasUpper],
          entry[aliasCamel],
          entry[aliasPascal],
          entry.id,
          entry.ID
        );

        if (identifier === undefined || identifier === null || identifier === '') {
          return null;
        }

        return { [key]: String(identifier) };
      }

      return { [key]: String(entry) };
    })
    .filter(Boolean);

  if (normalized.length === 0) {
    return arrayValue.length === 0 ? [] : undefined;
  }

  return normalized;
}

function mapSoftDeletePayload(value) {
  if (value === undefined || value === null) {
    return undefined;
  }

  if (typeof value === 'boolean') {
    return { deleted: value };
  }

  if (typeof value !== 'object') {
    return undefined;
  }

  const deleted = coalesce(value.deleted, value.DELETED, value.is_deleted, value.IS_DELETED);
  const deletedAt = coalesce(value.deleted_at, value.DELETED_AT);

  const result = {};

  if (deleted !== undefined) {
    result.deleted = !!deleted;
  }

  if (deletedAt !== undefined) {
    result.deleted_at = deletedAt;
  }

  if (result.deleted && result.deleted_at === undefined) {
    result.deleted_at = null;
  }

  return Object.keys(result).length ? result : undefined;
}

function mapAuditPayload(value) {
  if (!value || typeof value !== 'object') {
    return undefined;
  }

  const audit = {};
  ['created_by', 'created_at', 'updated_by', 'updated_at'].forEach((key) => {
    const aliasUpper = key.toUpperCase();
    const val = coalesce(value[key], value[aliasUpper]);
    if (val !== undefined) {
      audit[key] = val;
    }
  });

  return Object.keys(audit).length ? audit : undefined;
}

function buildSemanticModelPayload(source = {}) {
  if (source === null || typeof source !== 'object') {
    throw new Error('SemanticModelService: payload debe ser un objeto');
  }

  const userAccoutId = coalesce(
    source.user_accout_id,
    source.user_account_id,
    source.USER_ACCOUT_ID,
    source.USER_ACCOUNT_ID,
    source.account_id,
    source.ACCOUNT_ID
  );

  if (!userAccoutId) {
    throw new Error('SemanticModelService: user_accout_id es requerido');
  }

  const semantidDatabaseId = coalesce(
    source.semantid_database_id,
    source.semantic_database_id,
    source.SEMANTID_DATABASE_ID,
    source.SEMANTIC_DATABASE_ID,
    source.semanticDatabaseId
  );

  if (!semantidDatabaseId) {
    throw new Error('SemanticModelService: semantid_database_id es requerido');
  }

  const rawName = coalesce(source.name, source.MODEL_NAME);

  if (!rawName || String(rawName).trim().length < NAME_MIN_LENGTH) {
    throw new Error('SemanticModelService: name es requerido y debe tener al menos 3 caracteres');
  }

  const trimmedName = String(rawName).trim();

  if (trimmedName.length > NAME_MAX_LENGTH) {
    throw new Error(`SemanticModelService: name debe tener máximo ${NAME_MAX_LENGTH} caracteres`);
  }

  const payload = {
    user_accout_id: String(userAccoutId),
    semantid_database_id: String(semantidDatabaseId),
    name: trimmedName,
  };

  const description = coalesce(source.description, source.DESCRIPTION);
  if (description !== undefined) {
    payload.description = String(description);
  }

  const isPublic = coalesce(source.is_public, source.IS_PUBLIC);
  if (isPublic !== undefined) {
    payload.is_public = !!isPublic;
  }

  const isDeleted = coalesce(source.is_deleted, source.IS_DELETED);
  if (isDeleted !== undefined) {
    payload.is_deleted = !!isDeleted;
  }

  const isActive = coalesce(source.is_active, source.IS_ACTIVE);
  if (isActive !== undefined) {
    payload.is_active = !!isActive;
  }

  const versionId = coalesce(source.version_id, source.VERSION_ID);
  if (versionId !== undefined) {
    payload.version_id = String(versionId);
  }

  const semanticModelId = coalesce(
    source.semantic_model_id,
    source.semanticModelId,
    source.ID,
    source.id
  );
  if (semanticModelId !== undefined) {
    payload.semantic_model_id = String(semanticModelId);
  }

  const containedTablesRaw = coalesce(
    source.contained_tables_id,
    source.CONTAINED_TABLES_ID,
    source.contained_tables,
    source.CONTAINED_TABLES
  );
  const containedTables = mapArrayOfIdentifiers(containedTablesRaw, 'table_id');
  if (containedTables !== undefined) {
    payload.contained_tables_id = containedTables;
  }

  const usersAllowedRaw = coalesce(source.users_allowed, source.USERS_ALLOWED);
  const usersAllowed = mapArrayOfIdentifiers(usersAllowedRaw, 'user_id');
  if (usersAllowed !== undefined) {
    payload.users_allowed = usersAllowed;
  }

  const softDelete = mapSoftDeletePayload(coalesce(
    source.soft_delete,
    source.softDelete,
    source.SOFT_DELETE
  ));
  if (softDelete) {
    payload.soft_delete = softDelete;
  }

  const audit = mapAuditPayload(coalesce(source.audit, source.AUDIT));
  if (audit) {
    payload.audit = audit;
  }

  const allowedKeys = new Set([
    'semantic_model_id',
    'user_accout_id',
    'semantid_database_id',
    'name',
    'description',
    'contained_tables_id',
    'soft_delete',
    'audit',
    'users_allowed',
    'is_public',
    'is_deleted',
    'is_active',
    'version_id',
  ]);

  Object.keys(payload).forEach((key) => {
    if (!allowedKeys.has(key) || payload[key] === undefined) {
      delete payload[key];
    }
  });

  return payload;
}

function decorateSemanticModel(model) {
  if (!model || typeof model !== 'object') {
    return model;
  }

  const decorated = { ...model };

  const assignWithAliases = (canonical, aliases = [], transform) => {
    const value = coalesce(decorated[canonical], ...aliases.map((alias) => decorated[alias]));
    if (value === undefined) {
      return;
    }
    const finalValue = transform ? transform(value) : value;
    decorated[canonical] = finalValue;
    aliases.forEach((alias) => {
      decorated[alias] = finalValue;
    });
  };

  assignWithAliases('semantic_model_id', ['semanticModelId', 'ID', 'id'], (v) => String(v));
  assignWithAliases('user_accout_id', ['user_account_id', 'USER_ACCOUT_ID', 'USER_ACCOUNT_ID']);
  assignWithAliases('semantid_database_id', ['semantic_database_id', 'SEMANTID_DATABASE_ID', 'SEMANTIC_DATABASE_ID']);
  assignWithAliases('name', ['MODEL_NAME']);
  assignWithAliases('description', ['DESCRIPTION']);
  assignWithAliases('model_type', ['MODEL_TYPE']);
  assignWithAliases('is_public', ['IS_PUBLIC'], (v) => !!v);
  assignWithAliases('is_deleted', ['IS_DELETED'], (v) => !!v);
  assignWithAliases('is_active', ['IS_ACTIVE'], (v) => !!v);
  assignWithAliases('version_id', ['VERSION_ID']);

  const softSource = coalesce(decorated.soft_delete, decorated.softDelete, decorated.SOFT_DELETE);
  const softNormalized = mapSoftDeletePayload(softSource);
  if (softNormalized) {
    decorated.soft_delete = {
      deleted: softNormalized.deleted ?? false,
      deleted_at: softNormalized.deleted_at ?? null,
    };
    decorated.softDelete = decorated.soft_delete;
    decorated.SOFT_DELETE = {
      DELETED: decorated.soft_delete.deleted ?? false,
      DELETED_AT: decorated.soft_delete.deleted_at ?? null,
    };

    if (decorated.is_deleted === undefined && softNormalized.deleted !== undefined) {
      decorated.is_deleted = !!softNormalized.deleted;
      decorated.IS_DELETED = !!softNormalized.deleted;
    }
  }

  const tablesNormalized = mapArrayOfIdentifiers(
    coalesce(decorated.contained_tables_id, decorated.CONTAINED_TABLES_ID),
    'table_id'
  );
  if (tablesNormalized !== undefined) {
    decorated.contained_tables_id = tablesNormalized;
    decorated.CONTAINED_TABLES_ID = tablesNormalized;
  }

  const usersNormalized = mapArrayOfIdentifiers(
    coalesce(decorated.users_allowed, decorated.USERS_ALLOWED),
    'user_id'
  );
  if (usersNormalized !== undefined) {
    decorated.users_allowed = usersNormalized;
    decorated.USERS_ALLOWED = usersNormalized;
  }

  return decorated;
}

function adaptSemanticModelResponse(data) {
  if (Array.isArray(data)) {
    return data.map(decorateSemanticModel);
  }

  if (data && typeof data === 'object') {
    const result = { ...data };

    if (Array.isArray(result.items)) {
      result.items = result.items.map(decorateSemanticModel);
    }

    if (Array.isArray(result.semantic_models)) {
      result.semantic_models = result.semantic_models.map(decorateSemanticModel);
    }

    return decorateSemanticModel(result);
  }

  return data;
}

function normalizeSoftDeleteOptions(options) {
  if (options === undefined) {
    return { deleted: true };
  }

  if (typeof options === 'boolean') {
    return { deleted: options };
  }

  const body = mapSoftDeletePayload(options) || {};

  if (body.deleted === undefined) {
    body.deleted = true;
  }

  const deletedAt = coalesce(options.deleted_at, options.DELETED_AT);
  if (deletedAt !== undefined) {
    body.deleted_at = deletedAt;
  }

  const updatedBy = coalesce(
    options.updated_by,
    options.UPDATED_BY,
    options?.audit?.updated_by,
    options?.audit?.UPDATED_BY
  );

  if (updatedBy !== undefined) {
    body.updated_by = updatedBy;
  }

  return body;
}


//************ */
async function getAuthHeader() {
  try {
    const tokens = (await fetchAuthSession()).tokens;
    const idToken = tokens?.idToken?.toString();
    //const accessToken = tokens?.accessToken?.toString();

    if (idToken) {
      return { Authorization: `Bearer ${idToken}` };
    }
  } catch (error) {
    console.error('SemanticModelService.getAuthHeader: unable to fetch session', error);
  }

  return {};
}

async function fetchWithAuth(input, init = {}) {

  const authHeader = await getAuthHeader();

  const headers = {
    ...(init.body ? { 'Content-Type': 'application/json' } : {}),
    ...(init.headers || {}),
    //...defaultHeaders,
    ...authHeader
  };

  console.log('Fetching %s with headers: %o', JSON.stringify(input) , JSON.stringify(headers) );

  const response = await fetch(input, {
    ...init,
    headers,
    mode: 'cors',
    cache: 'no-cache'
  });

  
  console.log('Response from %s: %o', JSON.stringify(input), response );

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    const errorBody = await response.text().catch(() => '');
    throw new Error(`HTTP ${response.status}: ${errorBody || 'Unexpected error'}`);
  }

  if (response.status === 204) {
    return null;
  }

  const contentType = response.headers.get('content-type') || '';

  if (contentType.includes('application/json')) {
    return response.json();
  }

  const text = await response.text();
  try {
    return text ? JSON.parse(text) : null;
  } catch {
    return text || null;
  }
}
const SemanticModelService = {


  async list({ includeDeleted = false, limit, cursor, nextToken } = {}) {
    const qs = new URLSearchParams();

    if (includeDeleted) {
      qs.set('include_deleted', 'true');
    }

    if (limit !== undefined && limit !== null) {
      qs.set('limit', String(limit));
    }

    // El backend acepta `cursor` y sigue aceptando `next_token` como
    // alias. `adaptSemanticModelResponse` hace spread de la respuesta, así
    // que `next_cursor` llega intacto al caller.
    if (cursor || nextToken) {
      qs.set('cursor', cursor || nextToken);
    }

    const query = qs.toString();
    const url = query ? `${BASE_URL}?${query}` : BASE_URL;
    const response = await fetchWithAuth(url, { method: 'GET' });
    return adaptSemanticModelResponse(response);
  },

  async getById(id) {
    if (!id) {
      throw new Error('SemanticModelService.getById: id es requerido');
    }

    const response = await fetchWithAuth(`${BASE_URL}/${encodeURIComponent(id)}`, { method: 'GET' });
    return adaptSemanticModelResponse(response);
  },

  async create(payload) {
    const body = buildSemanticModelPayload(payload);

    const response = await fetchWithAuth(BASE_URL, {
      method: 'POST',
      body: JSON.stringify(body),
    });

    return adaptSemanticModelResponse(response);
  },

  async update(id, payload) {
    if (!id) {
      throw new Error('SemanticModelService.update: id es requerido');
    }

    const body = buildSemanticModelPayload({
      ...payload,
      semantic_model_id: payload?.semantic_model_id ?? id,
    });

    const response = await fetchWithAuth(`${BASE_URL}/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });

    return adaptSemanticModelResponse(response);
  },

  async softDelete(id, options = {}) {
    if (!id) {
      throw new Error('SemanticModelService.softDelete: id es requerido');
    }

    const body = normalizeSoftDeleteOptions(options);

    const response = await fetchWithAuth(`${BASE_URL}/${encodeURIComponent(id)}/soft-delete`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });

    return adaptSemanticModelResponse(response);
  },
};

export default SemanticModelService;
